import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import { MUTED_LABEL_COLOR, HEADING_TEXT_COLOR } from './styleConstants';

const STAT_BOX_SX = {
  minHeight: '116px',
  padding: '18px',
  border: '1px solid #e3e8ef',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(15, 32, 49, 0.08)'
};

const STAT_LABEL_SX = {
  display: 'block',
  marginBottom: '10px',
  color: MUTED_LABEL_COLOR,
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase'
};

const STAT_VALUE_SX = {
  display: 'block',
  color: HEADING_TEXT_COLOR,
  fontSize: '23px',
  fontWeight: 800
};

const InvoiceSummaryStats = ({ amountIvaIncluded, pendingAmount }) => {
  const { t } = useTranslation();
  const hasInvoice = pendingAmount !== null;
  const paidAmount = hasInvoice ? amountIvaIncluded - pendingAmount : null;
  const paidPercent =
    hasInvoice && amountIvaIncluded > 0
      ? Math.min(100, Math.max(0, Math.round((paidAmount / amountIvaIncluded) * 100)))
      : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '14px',
        marginBottom: '18px'
      }}
    >
      <Box sx={STAT_BOX_SX}>
        <Typography component="span" sx={STAT_LABEL_SX}>
          {t('TRANSACTION.DETAIL.INVOICE_AMOUNT')}
        </Typography>
        <Typography component="strong" sx={STAT_VALUE_SX}>
          {formatPositiveAmount(amountIvaIncluded)}
        </Typography>
      </Box>
      <Box sx={STAT_BOX_SX}>
        <Typography component="span" sx={STAT_LABEL_SX}>
          {t('TRANSACTION.DETAIL.PAID_AMOUNT')}
        </Typography>
        <Typography component="strong" sx={STAT_VALUE_SX}>
          {hasInvoice ? formatPositiveAmount(paidAmount) : '—'}
        </Typography>
      </Box>
      <Box sx={STAT_BOX_SX}>
        <Typography component="span" sx={STAT_LABEL_SX}>
          {t('TRANSACTION.DETAIL.PENDING_AMOUNT')}
        </Typography>
        <Typography component="strong" sx={STAT_VALUE_SX}>
          {!hasInvoice
            ? '—'
            : pendingAmount === 0
            ? t('TRANSACTION.LIQUIDATED')
            : formatPositiveAmount(pendingAmount)}
        </Typography>
        {hasInvoice && (
          <Box
            aria-label={`${paidPercent}% ${t(
              'TRANSACTION.DETAIL.PAID_PERCENT_LABEL'
            )}`}
            sx={{
              height: '8px',
              borderRadius: '999px',
              backgroundColor: '#e9eef4',
              marginTop: '17px',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                height: '100%',
                borderRadius: 'inherit',
                backgroundColor: '#7cb342',
                width: `${paidPercent}%`
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

InvoiceSummaryStats.propTypes = {
  amountIvaIncluded: PropTypes.number.isRequired,
  pendingAmount: PropTypes.number
};

InvoiceSummaryStats.defaultProps = {
  pendingAmount: null
};

export default InvoiceSummaryStats;
